import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import ProductSuggestionForm from "@/components/ProductSuggestionForm";
import { User, LogOut, LayoutDashboard, AlignJustify, Settings, Mail } from "lucide-react";
import { cn } from "@/lib/utils";
import { scrollPageToTopSoon } from "@/lib/scroll";
import BrandLogo from "@/components/BrandLogo";

const mobLinkCls =
  "flex min-h-11 items-center rounded-lg px-3 text-base font-medium text-slate-700 hover:bg-slate-50 hover:text-[#2D7FF9] active:bg-slate-100";

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [writeUsOpen, setWriteUsOpen] = useState(false);
  /** Homepage : formulaire contact en bas — pas de doublon « Nous écrire ». */
  const showWriteUs = Boolean(user && user.role !== "admin" && loc.pathname !== "/");

  const handleLogoClick = (e) => {
    e.preventDefault();
    setMobileOpen(false);
    const atHome = loc.pathname === "/" || loc.pathname === "";
    if (!atHome || loc.hash) {
      navigate("/", { replace: atHome });
    }
    scrollPageToTopSoon();
  };

  return (
    <>
      {/* fixed (pas sticky) : Radix Select verrouille le scroll du body (RemoveScroll) et casse le sticky sur les formulaires denses */}
      <header className="fixed inset-x-0 top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#E5E7EB] shadow-sm saturate-150 supports-[backdrop-filter]:bg-white/70">
        <div className="max-w-7xl mx-auto grid min-h-16 grid-cols-[1fr_auto_1fr] items-center gap-3 px-4 sm:px-6">
          {loc.pathname === "/" || loc.pathname === "" ? (
            <Link
              to="/"
              onClick={handleLogoClick}
              className="flex min-w-0 shrink items-center justify-self-start gap-1.5 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-[#2D7FF9]/30"
              data-testid="nav-logo"
            >
              <span className="font-display font-black text-[17px] tracking-[-0.03em] text-[#0B0D12] sm:text-[22px]">
                RdvMarket
              </span>
              <span className="mt-2.5 h-2 w-2 rounded-full bg-[#2D7FF9]" aria-hidden />
            </Link>
          ) : (
            <BrandLogo
              to="/"
              size="sm"
              showWordmark
              wordmarkClassName="font-black text-[17px] tracking-[-0.03em] text-[#0B0D12] sm:text-[22px]"
              linkClassName="justify-self-start shrink min-w-0"
              onClick={handleLogoClick}
              data-testid="nav-logo"
            />
          )}

          <nav
            className="col-start-2 hidden items-center justify-self-center gap-6 text-sm font-medium text-slate-700 lg:gap-8 md:flex"
            aria-label="Navigation principale"
          >
            <Link to={{ pathname: "/", hash: "comment" }} className="hover:text-[#2D7FF9] transition-colors text-slate-700" data-testid="nav-how">Comment ça marche</Link>
            <Link
              to="/marketplace"
              className={`hover:text-[#2D7FF9] transition-colors ${["/marketplace", "/catalogue"].includes(loc.pathname) ? "text-[#2D7FF9]" : "text-slate-700"}`}
              data-testid="nav-marketplace"
            >
              Marketplace
            </Link>
            <Link to={{ pathname: "/", hash: "contact" }} className="hover:text-[#2D7FF9] transition-colors text-slate-700" data-testid="nav-contact">Contact</Link>
          </nav>

          <div className="col-start-3 flex min-w-0 items-center justify-end gap-2 sm:gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  className="inline-flex md:hidden h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-[#E5E7EB] text-slate-700 hover:bg-slate-50"
                  aria-label="Ouvrir le menu de navigation"
                  aria-expanded={mobileOpen}
                >
                  <AlignJustify className="h-5 w-5" strokeWidth={2} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[min(100vw-1.5rem,22rem)] p-0 sm:p-6">
                <SheetHeader className="border-b border-[#E5E7EB] p-6 pb-4 text-left">
                  <SheetTitle className="font-display text-lg">Navigation</SheetTitle>
                  <SheetDescription className="sr-only">
                    Liens vers les sections du site : fonctionnement, marketplace, contact.
                  </SheetDescription>
                </SheetHeader>
                <nav className="flex flex-col gap-1 p-4" aria-label="Navigation mobile">
                  <Link to={{ pathname: "/", hash: "comment" }} className={mobLinkCls} onClick={() => setMobileOpen(false)}>
                    Comment ça marche
                  </Link>
                  <Link to="/marketplace" className={mobLinkCls} onClick={() => setMobileOpen(false)}>
                    Marketplace
                  </Link>
                  <Link to={{ pathname: "/", hash: "contact" }} className={mobLinkCls} onClick={() => setMobileOpen(false)}>
                    Contact
                  </Link>
                </nav>

                {!user ? (
                  <div className="flex flex-col gap-2 border-t border-[#E5E7EB] p-4 pb-8">
                    <Button asChild variant="outline" className="h-12 w-full justify-center rounded-lg" onClick={() => setMobileOpen(false)}>
                      <Link to="/login">Connexion</Link>
                    </Button>
                    <Button asChild className="btn-primary h-12 w-full justify-center rounded-lg" onClick={() => setMobileOpen(false)}>
                      <Link to="/register">Créer un compte</Link>
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-1 border-t border-[#E5E7EB] p-4 pb-8">
                    {user.role !== "admin" && (
                      <button
                        type="button"
                        className={mobLinkCls}
                        onClick={() => {
                          setMobileOpen(false);
                          setWriteUsOpen(true);
                        }}
                        data-testid="nav-write-us-mobile"
                      >
                        <Mail className="mr-2 h-4 w-4" />
                        Nous écrire
                      </button>
                    )}
                    <button type="button" className={mobLinkCls} onClick={() => { setMobileOpen(false); navigate("/marketplace"); }}>
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      Marketplace
                    </button>
                    <button type="button" className={mobLinkCls} onClick={() => { setMobileOpen(false); navigate("/compte"); }}>
                      <Settings className="mr-2 h-4 w-4" />
                      Modifier mes infos
                    </button>
                    {user.role === "admin" && (
                      <button type="button" className={mobLinkCls} onClick={() => { setMobileOpen(false); navigate("/admin"); }}>
                        <User className="mr-2 h-4 w-4" />
                        Administration
                      </button>
                    )}
                    <button
                      type="button"
                      className={cn(mobLinkCls, "mt-2 text-red-600 hover:bg-red-50 hover:text-red-700")}
                      onClick={async () => {
                        setMobileOpen(false);
                        await logout();
                        navigate("/");
                      }}
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Déconnexion
                    </button>
                  </div>
                )}
              </SheetContent>
            </Sheet>

            {showWriteUs ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="hidden shrink-0 gap-1.5 text-slate-600 hover:bg-slate-100 hover:text-slate-900 md:inline-flex"
                onClick={() => setWriteUsOpen(true)}
                data-testid="nav-write-us"
              >
                <Mail className="h-4 w-4" aria-hidden />
                <span className="hidden sm:inline">Nous écrire</span>
              </Button>
            ) : null}

            {user ? (
              <Link
                to="/marketplace"
                className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#2D7FF9] to-[#1a5fd4] text-xs font-bold text-white no-underline outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-[#2D7FF9]/35 focus-visible:ring-offset-2 md:inline-flex sm:h-9 sm:w-9"
                data-testid="nav-dashboard-link"
                aria-label="Marketplace"
                title="Marketplace"
              >
                {user.name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || "U"}
              </Link>
            ) : (
              <div className="hidden items-center gap-2 md:flex">
                <Button asChild variant="ghost" className="hover:text-[#2D7FF9]" data-testid="nav-login">
                  <Link to="/login">Connexion</Link>
                </Button>
                <Button asChild className="btn-primary rounded-md" data-testid="nav-register">
                  <Link to="/register">Créer un compte</Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>
      <div className="h-16 shrink-0" aria-hidden="true" />

      <Dialog open={writeUsOpen} onOpenChange={setWriteUsOpen}>
        <DialogContent className="max-h-[min(90vh,720px)] w-[calc(100vw-1.5rem)] max-w-lg overflow-y-auto sm:rounded-2xl" data-testid="nav-write-us-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Nous écrire</DialogTitle>
            <DialogDescription className="text-left text-sm text-slate-600">
              Question simple, partenariat, idée de produit : choisissez le type d’échange, nous lisons tout.
            </DialogDescription>
          </DialogHeader>
          <ProductSuggestionForm context="navbar" onSubmitted={() => setWriteUsOpen(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Navbar;
